import _              from 'underscore';
import { useHistory } from 'react-router';

import { AdminActivityManagerParamsBySection, AdminBusinessFunctionManagerParamsBySection, AdminHireTypeManagerParamsBySection, AdminObjectiveManagerParamsBySection, AdminProfessionalFeeManagerParamsBySection, RouterLocationStates, ROUTING_INTENTS, Uuid } from '../types';

import { RouterUtility } from '../util';


type UseEntityRoutingHooksPathingMethod = ( entityId: Uuid ) => string

interface UseEntityRoutingHooks {

    updateRouterURLSilently: ( url: string ) => void,

    pushRouterToEntity: ( entityRoutingPushObject: RouterLocationStates.PushToEntityBasedRouteState ) => void,

    isRouteStatePushingToEntity: ( locationStateObject: RouterLocationStates.IntentBasedRouteState ) => void,

    getDEMObjectivePath: UseEntityRoutingHooksPathingMethod,

    getDEMObjectiveByGroupPath: UseEntityRoutingHooksPathingMethod,

    getDEMObjectiveGroupPath: UseEntityRoutingHooksPathingMethod,

    getDEMObjectiveByNamePath: UseEntityRoutingHooksPathingMethod,

    getDEMActivityPath: UseEntityRoutingHooksPathingMethod,

    getDEMActivityByObjectivePath: UseEntityRoutingHooksPathingMethod,

    getDEMActivityByNamePath: UseEntityRoutingHooksPathingMethod,

    getDEMBusinessFunctionByNamePath: UseEntityRoutingHooksPathingMethod,
    getDEMBusinessFunctionByRefCodePath: UseEntityRoutingHooksPathingMethod,

    getDEMProfessionalFeeByNamePath: UseEntityRoutingHooksPathingMethod,
    getDEMProfessionalFeeByCodePath: UseEntityRoutingHooksPathingMethod,

    getDEMHireTypeByNamePath: UseEntityRoutingHooksPathingMethod,
    getDEMHireTypeByCodePath: UseEntityRoutingHooksPathingMethod,
    getDEMHireTypeByFunctionPath: UseEntityRoutingHooksPathingMethod,
}

function useEntityRouting(): UseEntityRoutingHooks{

    let { push } = useHistory();

    // Silently updates the url outside of the router
    const updateRouterURLSilently: UseEntityRoutingHooks['updateRouterURLSilently'] = ( url: string ) => {

        window.history.replaceState( null, null, url );
    };

    // Pushes a path to router with explicit intention of targeting an entity
    const pushRouterToEntity: UseEntityRoutingHooks['pushRouterToEntity'] = ( entityRoutingPushObject ) => {

        let { path, ...state } = entityRoutingPushObject;

        push( path, { intent: ROUTING_INTENTS.TARGET_ENTITY, ...state } );
    };

    // Checks if a route has state indicating it was attempting to target an entity
    const isRouteStatePushingToEntity: UseEntityRoutingHooks['isRouteStatePushingToEntity'] = ( state ) => {

        if( !_.isNull( state?.intent ) &&
            !_.isUndefined( state?.intent ) ){

            return ( state.intent === ROUTING_INTENTS.TARGET_ENTITY );
        }

        return false;
    };

    // Convenience method for the default path to an objective in the DEM
    const getDEMObjectivePath: UseEntityRoutingHooks['getDEMObjectivePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.OBJECTIVES.SECTION', { section: AdminObjectiveManagerParamsBySection.BY_CATEGORY, entityId } );
    };

    // Convenience method for the path to an objective within a group in the DEM
    const getDEMObjectiveByGroupPath: UseEntityRoutingHooks['getDEMObjectivePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.OBJECTIVES.SECTION', { section: AdminObjectiveManagerParamsBySection.BY_GROUP, entityId } );
    };

    // Convenience method for the default path to an objective group in the DEM
    const getDEMObjectiveGroupPath: UseEntityRoutingHooks['getDEMObjectivePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.OBJECTIVES.SECTION', { section: AdminObjectiveManagerParamsBySection.GROUPS, entityId } );
    };
    // Convenience method for the default path to an objective group in the DEM
    const getDEMObjectiveByNamePath: UseEntityRoutingHooks['getDEMObjectiveByNamePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.OBJECTIVES.SECTION', { section: AdminObjectiveManagerParamsBySection.BY_NAME, entityId } );
    };

    // Convenience method for the default path to an objective in the DEM
    const getDEMActivityPath: UseEntityRoutingHooks['getDEMActivityPath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.ACTIVITIES.SECTION', { section: AdminActivityManagerParamsBySection.BY_CATEGORY, entityId } );
    };

    // Convenience method for the default path to an objective in the DEM
    const getDEMActivityByObjectivePath: UseEntityRoutingHooks['getDEMActivityByObjectivePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.ACTIVITIES.SECTION', { section: AdminActivityManagerParamsBySection.BY_OBJECTIVE, entityId } );
    };

    // Convenience method for the path to an objective within a group in the DEM
    const getDEMActivityByNamePath: UseEntityRoutingHooks['getDEMActivityByNamePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.ACTIVITIES.SECTION', { section: AdminActivityManagerParamsBySection.BY_NAME, entityId } );
    };

    // Convenience method for the path to a business function category by name in the DEM
    const getDEMBusinessFunctionByNamePath: UseEntityRoutingHooks['getDEMBusinessFunctionByNamePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.RESOURCES.BUSINESS_FUNCTIONS.SECTION', { section: AdminBusinessFunctionManagerParamsBySection.BY_NAME, entityId } );
    };

    // Convenience method for the path to a business function category by ref code in the DEM
    const getDEMBusinessFunctionByRefCodePath: UseEntityRoutingHooks['getDEMBusinessFunctionByRefCodePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.RESOURCES.BUSINESS_FUNCTIONS.SECTION', { section: AdminBusinessFunctionManagerParamsBySection.BY_REF_CODE, entityId } );
    };

    // Convenience method for the path to a professional fee resource by name in the DEM
    const getDEMProfessionalFeeByNamePath: UseEntityRoutingHooks['getDEMProfessionalFeeByNamePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.RESOURCES.FEES.SECTION', { section: AdminProfessionalFeeManagerParamsBySection.BY_NAME, entityId } );
    };

    // Convenience method for the path to a professional fee resource by code in the DEM
    const getDEMProfessionalFeeByCodePath: UseEntityRoutingHooks['getDEMProfessionalFeeByCodePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.RESOURCES.FEES.SECTION', { section: AdminProfessionalFeeManagerParamsBySection.BY_RESOURCE_CODE, entityId } );
    };

    // Convenience method for the path to a professional fee resource by name in the DEM
    const getDEMHireTypeByNamePath: UseEntityRoutingHooks['getDEMHireTypeByNamePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.RESOURCES.HIRE_TYPES.SECTION', { section: AdminHireTypeManagerParamsBySection.BY_NAME, entityId } );
    };

    // Convenience method for the path to a professional fee resource by code in the DEM
    const getDEMHireTypeByCodePath: UseEntityRoutingHooks['getDEMHireTypeByCodePath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.RESOURCES.HIRE_TYPES.SECTION', { section: AdminHireTypeManagerParamsBySection.BY_RESOURCE_CODE, entityId } );
    };

    // Convenience method for the path to a professional fee resource by function in the DEM
    const getDEMHireTypeByFunctionPath: UseEntityRoutingHooks['getDEMHireTypeByFunctionPath'] = ( entityId ) => {

        return RouterUtility.getRoute( 'ADMIN.DECISION_ENGINE.RESOURCES.HIRE_TYPES.SECTION', { section: AdminHireTypeManagerParamsBySection.BY_FUNCTION, entityId } );
    };

    return {
        updateRouterURLSilently,
        pushRouterToEntity,
        isRouteStatePushingToEntity,
        getDEMObjectivePath,
        getDEMObjectiveByGroupPath,
        getDEMObjectiveGroupPath,
        getDEMObjectiveByNamePath,
        getDEMActivityPath,
        getDEMActivityByObjectivePath,
        getDEMActivityByNamePath,
        getDEMBusinessFunctionByNamePath,
        getDEMBusinessFunctionByRefCodePath,
        getDEMProfessionalFeeByNamePath,
        getDEMProfessionalFeeByCodePath,
        getDEMHireTypeByNamePath,
        getDEMHireTypeByCodePath,
        getDEMHireTypeByFunctionPath,
    };
}

export { useEntityRouting };
